import { ReactNode } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useFilterData } from './FiltersProvider';
import { hasActiveFilters } from '../filterHelpers';
import FilterLayout from './FilterLayout';
import FilterControls from './FilterControls';
import ClearFiltersButton from './ClearFiltersButton';
import SelectedFiltersDisplay from './SelectedFiltersDisplay';
import FilterToastNotification from './FilterToastNotification';
import { AnalyticsCategory } from '../../config';
import { useIsMobile } from '../../../../hooks/useIsMobile';

export interface FilterBarProps {
  filters: BaseAnalyticsFilter;
  onFiltersChange: (filters: BaseAnalyticsFilter) => void;
  onClearFilters?: () => void;
  category?: AnalyticsCategory;
  className?: string;
  actions?: ReactNode;
}

export default function FilterBar({
  filters,
  onFiltersChange,
  onClearFilters,
  category,
  className = '',
  actions,
}: FilterBarProps) {
  const isMobile = useIsMobile();
  const { getUnitName, getExperienceName } = useFilterData();
  const hasFiltersActive = hasActiveFilters(filters);

  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    } else {
      const newFilters: BaseAnalyticsFilter = {
        experienceIds: [],
        unitIds: [],
        experienceTitle: undefined,
        granularity: 'month',
        startDate: undefined,
        endDate: undefined,
      };

      onFiltersChange(newFilters);
    }
  };

  return (
    <>
      <FilterLayout className={className}>
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'flex-row items-center gap-4'}`}>
          <div className="flex-1">
            <FilterControls
              filters={filters}
              onFiltersChange={onFiltersChange}
              category={category}
            />
          </div>

          {(hasFiltersActive || actions) && (
            <div
              className={`flex items-center gap-2 ${isMobile ? 'justify-center' : 'flex-shrink-0'}`}
            >
              {hasFiltersActive && <ClearFiltersButton onClear={handleClearFilters} />}
              {actions && !isMobile && <span aria-hidden className="mx-1 h-6 w-px bg-white/15" />}
              {actions}
            </div>
          )}
        </div>
      </FilterLayout>

      <FilterToastNotification filters={filters} />

      <SelectedFiltersDisplay
        filters={filters}
        onFiltersChange={onFiltersChange}
        getUnitName={getUnitName}
        getExperienceName={getExperienceName}
      />
    </>
  );
}
